import { Tooltip } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { compose } from "recompose";
import { ButtonComponent } from "../../../components/Comman/ButtonComponent";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import { withRouter } from "../../../components/withRouter";
import { blueDarkFades, greenDarkShade, lightSoftWhiteFaded, softYellowWhiteFade } from "../../../config/ColorObj";
import endpoint from "../../../config/endpoints";
import { areYouSureMsg, challanSettledMsg, challanSettleWarningMsg, checkBothDriverAndSettlerMsg, noInternetMsg, saveFailedMsg } from "../../../config/messageconstant";
import { apiPost } from "../../../utils/api_service";
import { showLoador, showNotification } from "../../Landing/Landing.slice";
import { DEFAULT_STEPS } from "../Constants/ChallanSetllementConstants";
import ValidationModal from "../Constants/ValidationModal";
import { Icons } from "../icons/Icons";
import {
    colors,
    getAvatarStyle,
    getBtnSubmitStyle,
    getDotStyle,
    getPillStyle,
    getSignerRowStyle,
    pageLayoutStyle,
    S,
    signaturesCardStyles,
    signerCardStyles,
    signoffStyles,
    topGridStyle,
} from "../styles/ChallanSettlementStyles";
import { InfoCard, SectionCard } from "../utils/index";

/**
 * Renders a single signer row with a checkbox, avatar, name, and subtitle.
 *
 * @param {object}   props
 * @param {object}   props.signer    - Signer config from SIGNERS constant.
 * @param {boolean}  props.checked   - Whether this signer has signed.
 * @param {boolean}  props.isLast    - Suppresses the right border on last item.
 * @param {Function} props.onChange  - Called with (checked: boolean).
 */
const SignerCard = memo(({ signer, checked, isLast, onChange }) => {
    const { name, sub, initials, avatarBg, avatarColor } = signer;

    // Styles that depend on props are memoised to prevent object churn.
    const rowStyle = useMemo(() => getSignerRowStyle(isLast), [isLast]);
    const avatarStyle = useMemo(
        () => getAvatarStyle(avatarBg, avatarColor),
        [avatarBg, avatarColor],
    );

    const handleChange = useCallback(
        (e) => onChange(e.target.checked),
        [onChange],
    );

    return (
        <div style={rowStyle}>
            <input
                type="checkbox"
                checked={checked}
                onChange={handleChange}
                style={signerCardStyles.checkbox}
            />

            <div style={avatarStyle}>{initials}</div>

            <div>
                <div style={signerCardStyles.name}>{name}</div>
                <div style={signerCardStyles.sub}>{sub}</div>
            </div>
        </div>
    );
});

SignerCard.displayName = "SignerCard";

/**
 * Displays a coloured status pill for a given signer.
 *
 * @param {object}  props
 * @param {string}  props.signerKey - "driver" | "settler"
 * @param {boolean} props.done      - Whether the signer has completed signing.
 */
const StatusPill = memo(({ signerKey, done }) => {
    const pillStyle = useMemo(() => getPillStyle(done), [done]);
    const dotStyle = useMemo(() => getDotStyle(done), [done]);

    const label = done
        ? `${signerKey === "driver" ? "Driver" : "Settler"} signed`
        : `Awaiting ${signerKey}`;

    return (
        <TypographyCompo style={pillStyle}>
            <TypographyCompo style={dotStyle} />
            {label}
        </TypographyCompo>
    );
});

StatusPill.displayName = "StatusPill";

/**
 * Card that houses all signer rows and their status pills.
 *
 * @param {object}   props
 * @param {object}   props.signChecks    - { driver: boolean, settler: boolean }
 * @param {Function} props.onCheckChange - Called with (signerKey, checked).
 */
const SignaturesCard = memo(({ signChecks, onCheckChange, signers }) => (
    <div style={signaturesCardStyles.card}>
        <div style={signaturesCardStyles.header}>Signatures required</div>

        <div style={signaturesCardStyles.grid}>
            {signers.map((signer, index) => (
                <SignerCard
                    key={signer.key}
                    signer={signer}
                    checked={signChecks[signer.key]}
                    isLast={index === signers.length - 1}
                    onChange={(value) => onCheckChange(signer.key, value)}
                />
            ))}
        </div>

        <div style={signaturesCardStyles.footer}>
            {signers.map(({ key }) => (
                <StatusPill key={key} signerKey={key} done={signChecks[key]} />
            ))}
        </div>
    </div>
));

SignaturesCard.displayName = "SignaturesCard";


const Signoff = memo(({
    hasPhysicalReturn,
    challanData,
    enrichedRows,
    showLoador,
    showNotification
}) => {

    const navigate = useNavigate();
    const [finalRemarks, setFinalRemarks] = useState("");
    const [signChecks, setSignChecks] = useState({
        driver: false,
        settler: false,
    });
    const [valOpen, setValOpen] = useState(false);

    // ── Derived ───────────────────────────────────────────────────────────────

    const isSignComplete = signChecks.driver && signChecks.settler;

    // Memoised only because it feeds a Tooltip title prop (avoids re-renders).
    const tooltipTitle = useMemo(
        () => (!isSignComplete ? checkBothDriverAndSettlerMsg : ""),
        [isSignComplete],
    );

    // Dynamic button style; memoised to prevent child re-renders.
    const btnSubmitStyle = useMemo(
        () => getBtnSubmitStyle(isSignComplete),
        [isSignComplete],
    );

    // Static value style lives in the styles file; colour token used directly.
    const settlementValueStyle = useMemo(() => ({ color: colors.amberMid }), []);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleCheckChange = useCallback((key, checked) => {
        setSignChecks((prev) => ({ ...prev, [key]: checked }));
    }, []);

    const handleRemarksChange = useCallback((e) => {
        setFinalRemarks(e.target.value);
    }, []);

    const openValidationModal = useCallback(() => setValOpen(true), []);
    const closeValidationModal = useCallback(() => setValOpen(false), []);

    const handleSave = useCallback(() => {
        const enrichedRowsMap = new Map(
            enrichedRows.map((row) => [row.id, row])
        );

        const updatedVanStockTransferDetail =
            challanData?.vanStockTransferDetail?.map((item) => {
                const matchedRow = enrichedRowsMap.get(item.id);

                return matchedRow
                    ? {
                        ...item,
                        secondaryDiffQty: Number(matchedRow.secondaryDiffQty) || 0,
                    }
                    : item;
            });

        const payload = {
            ...challanData,
            vanStockTransferDetail: updatedVanStockTransferDetail,
        };

        if (navigator.onLine) {
            swal({
                title: areYouSureMsg,
                text: challanSettleWarningMsg,
                icon: "warning",
                buttons: true,
                dangerMode: true,
            }).then(async (willDelete) => {
                if (willDelete) {
                    showLoador({ loador: true });
                    await apiPost({
                        url: endpoint.vanStockTransferHeader + "/day-end-details-save",
                        postBody: payload,
                    }).then(async ({ data, success }) => {

                        console.log("data-", data);

                        if (success) {
                            showNotification({
                                msg: data?.errorMessage || challanSettledMsg,
                            });

                            await navigate(-1);

                        } else {
                            showNotification({
                                msg: data?.errorMessage || saveFailedMsg,
                                severity: "error",
                            });
                        }
                    });
                    showLoador({ loador: false });
                }
            });
        } else {
            showNotification({ msg: noInternetMsg, severity: "error" });
        }

    }, [challanData, enrichedRows]);

    const signers = useMemo(
        () => [
            {
                key: "driver",
                name: challanData?.driverName || "",
                sub: challanData?.vehicleNo || "",
                initials: challanData?.driverName
                    ?.split(" ")
                    ?.map((x) => x[0])
                    ?.join("")
                    ?.toUpperCase() || "DR",
                avatarBg: lightSoftWhiteFaded,
                avatarColor: blueDarkFades,
            },
            {
                key: "settler",
                name: challanData?.stockStakeHolderTypeName || "",
                sub: "Settlement officer",
                initials: challanData?.stockStakeHolderTypeName
                    ?.split(" ")
                    ?.map((x) => x[0])
                    ?.join("")
                    ?.toUpperCase() || "ST",
                avatarBg: softYellowWhiteFade,
                avatarColor: greenDarkShade,
            },
        ],
        [challanData]
    );



    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <SectionCard
            themeKey="signoff"
            icon={Icons.signoff}
            title="Settler sign-off & approval"
        >
            <div style={S.sectionBody} className="cs-section-body">
                <div style={pageLayoutStyle}>
                    {/* ── Top grid ── */}
                    <div style={topGridStyle}>
                        <InfoCard
                            label="Settled by"
                            value={challanData?.stockStakeHolderTypeName + " (Settler)"}
                            sub={challanData?.date}
                        />

                        <InfoCard
                            label="Settlement status"
                            value="Pending approval"
                            sub="Awaiting admin sign-off"
                            valueStyle={settlementValueStyle}
                        />

                        <SignaturesCard
                            signChecks={signChecks}
                            onCheckChange={handleCheckChange}
                            signers={signers}
                        />
                    </div>

                    {/*Comment by tejas (01-06-2026): As per Akash sir, Final settler remarks 
                    section is not required. Hence commenting out. */}

                    {/* ── Final remarks ── */}
                    {/* <div style={signoffStyles.remarkBlock}>
                        <div style={signoffStyles.fieldLabel}>Final settler remarks</div>

                        <textarea
                            style={signoffStyles.textarea}
                            className="cs-textarea"
                            value={finalRemarks}
                            onChange={handleRemarksChange}
                            placeholder="Overall settlement notes, special conditions, pending follow-ups, exceptions…"
                        />
                    </div> */}

                    {/* ── Action bar ── */}
                    <div style={signoffStyles.actionBar} className="cs-action-bar">
                        {/* Comment by tejas (02-06-2026): As per Akash sir, Print settlement sheet and Hold buttons are not required. Hence commenting out. */}
                        {/* <ButtonComponent style={signoffStyles.btn} className="cs-btn">
                            Print settlement sheet
                        </ButtonComponent>

                        <ButtonComponent
                            style={signoffStyles.btnHold}
                            className="cs-btn-hold"
                        >
                            Hold — discrepancy unresolved
                        </ButtonComponent> */}

                        <Tooltip title={tooltipTitle} arrow placement="top">
                            <div style={{ display: "inline-block" }}>
                                <ButtonComponent
                                    style={btnSubmitStyle}
                                    className="cs-btn-save"
                                    disabled={!isSignComplete}
                                    onClick={openValidationModal}
                                >
                                    Submit
                                </ButtonComponent>
                            </div>
                        </Tooltip>
                    </div>

                    {/* ── Validation modal ── */}
                    <ValidationModal
                        open={valOpen}
                        onClose={closeValidationModal}
                        onSave={handleSave}
                        challanNo={challanData?.transferNo}
                        driverName={challanData?.driverName}
                        settledBy={challanData?.stockStakeHolderTypeName}
                        steps={DEFAULT_STEPS}
                        hasPhysicalReturn={hasPhysicalReturn}
                    />
                </div>
            </div>
        </SectionCard>
    );
});

Signoff.displayName = "Signoff";


const mapStateToProps = (state) => ({
});
const mapDispatchToProps = {
    showLoador,
    showNotification,
};

export default compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
)(Signoff);